import { ElementType, ReactNode, useCallback, useState } from 'react';
import { useToggle } from 'react-use';

import SelfClickingLink from '~components/generic/SelfClickingLink';
import useAppSelector from '~hooks/useAppSelector';
import { RaiseRequestModalController } from '~modules/media-library/controller/RaiseRequestModalController';
import { AssetId } from '~modules/media-library/model/Asset';
import { shareFileMam } from '~services/api/calls/file';
import { useMamAssetUrl } from '~services/api/hooks/mam-assets.hooks';
import { selectConfigMyFilesEnabled } from '~store/selectors';
import { Download } from './Download';

type MamDownloadProps = {
  asset: MamAssetDto;
  description?: ReactNode;
  as?: ElementType;
};

export function MamDownload({ asset, description, as }: MamDownloadProps) {
  const isSharingEnabled = useAppSelector(selectConfigMyFilesEnabled);
  const [requestShare, setRequestShare] = useState(false);
  const [requestDownload, toggleRequestDownload] = useToggle(false);
  const { data: url } = useMamAssetUrl(asset.id, { enabled: requestDownload });
  const filename = asset.attributes.filename || `${asset.attributes.title}.${asset.attributes.fileextension}`;
  const previewUrl = asset.previews.details;
  const size = asset.attributes.filesize_formatted || '';
  const downloadAllowed = asset.actions.download;
  const myFiles = asset.actions.myFiles;
  const raiseRequest = asset.actions.raiseRequest && !asset.actions.download;
  const [idRaiseRequestForm, setIdRaiseRequestForm] = useState<AssetId>();

  // handle request to share asset
  const handleOnShare = useCallback(async () => {
    setRequestShare(true);
    await shareFileMam(asset.id);
    setRequestShare(false);
  }, [asset.id]);

  const handleOnRaiseRequestForm = useCallback(async () => {
    setIdRaiseRequestForm(asset.id.toString());
  }, [asset.id]);

  const closeRaiseRequest = useCallback(async () => {
    setIdRaiseRequestForm(null);
  }, []);

  return (
    <>
      <Download
        filename={filename}
        isSharingEnabled={isSharingEnabled}
        showDownload={downloadAllowed}
        showAddToMyFiles={myFiles}
        showRaiseRequest={raiseRequest}
        onShare={handleOnShare}
        onDownload={() => toggleRequestDownload()}
        onRaiseRequest={handleOnRaiseRequestForm}
        downloadLoading={requestDownload}
        shareLoading={requestShare}
        previewUrl={previewUrl}
        size={size}
        description={description}
        as={as}
      />
      {!!url && requestDownload && <SelfClickingLink url={url} onClicked={() => toggleRequestDownload()} />}
      <RaiseRequestModalController assetId={idRaiseRequestForm} onClose={closeRaiseRequest} />
    </>
  );
}
